import React from 'react'
import { flowRight } from 'lodash'
import { graphql } from '@apollo/client/react/hoc'
import { getVehicleVertical } from 'lib/utils/store/vehicle'
import { graphqlCurrentUserCached } from 'lib/utils/store/currentUser'
import { walkThroughtObjectValuesDeep } from 'lib/helpers'

import ActionLogic from 'components/Util/ActionLogic'

import deleteVehicle from 'graphql/vehicle/deleteVehicle.gql'
import updateVehiclePrefill from 'graphql/vehicle/updateVehiclePrefill.gql'
import updateVehicleMutation from 'graphql/vehicle/updateVehicle.gql'
import getVehicleProfileView from 'graphql/view/getVehicleProfileView.gql'
import updateVehicleStatus from 'graphql/vehicle/updateVehicleStatus.gql'
import createAdSketchForVehicle from 'graphql/vehicle/createAdSketchForVehicle.gql'
import requestTrafiData from 'graphql/vehicle/requestTrafiData.gql'
import updateImageCropPosition from 'graphql/vehicle/updateImageCropPosition.gql'

class VehicleProfileLogic extends React.Component {
    deleteVehicle = (actionLogic, vehicleId, isConfirmed) => {
        actionLogic.asyncSetState({
            loading: true,
            error: false,
        })

        return this.props.deleteVehicle({
            variables: { id: vehicleId, isConfirmed: isConfirmed },
        })
            .then(response => {
                actionLogic.asyncSetState({
                    loading: false,
                })
                return Promise.resolve(response)
            })
            .catch(error => {
                actionLogic.asyncSetState({
                    loading: false,
                    error: error,
                })
                return Promise.reject(error)
            })
    }

    updateVehiclePrefill = (actionLogic, vehicleId, regNumber) => {
        const { currentUser } = this.props

        actionLogic.asyncSetState({
            loading: true,
            error: false,
        })

        return this.props.updateVehiclePrefill({
            variables: { id: vehicleId, regNumber: regNumber.trim() },
            refetchQueries: [{
                query: getVehicleProfileView,
                variables: {
                    vehicleId: vehicleId,
                    userId: currentUser.me.id,
                }
            }],
            awaitRefetchQueries: true,
        })
            .then(response => {
                actionLogic.asyncSetState({
                    loading: false,
                })
                return Promise.resolve(response)
            })
            .catch(error => {
                actionLogic.asyncSetState({
                    loading: false,
                    error: error,
                })
                return Promise.reject(error)
            })
    }

    updateVehicle = (actionLogic, vehicleId, updateData) => {
        const { currentUser, updateVehicle } = this.props

        actionLogic.asyncSetState({
            loading: true,
            error: null,
        })

        updateData = {
            ...updateData,
        }

        // nullify zeros before submit
        const inputValues = walkThroughtObjectValuesDeep({ ...updateData }, (value) => {
            if (typeof value === 'boolean') return value
            if (value === 0) return value
            return (value && value !== '0') ? value : null
        })

        return updateVehicle({
            variables: {
                id: vehicleId,
                input: {
                    ...inputValues
                }
            },
            refetchQueries: [{
                query: getVehicleProfileView,
                variables: {
                    vehicleId: vehicleId,
                    userId: currentUser.me.id,
                }
            }],
            awaitRefetchQueries: true
        })
            .then(data => {
                actionLogic.asyncSetState({
                    loading: false,
                })
                return Promise.resolve(data)
            })
            .catch(error => {
                actionLogic.asyncSetState({
                    loading: false,
                    error: error,
                })
                return Promise.reject(error)
            })
    }

    updateVehicleStatus = async (actionLogic, vehicleId, status, soldInfo = null) => {
        actionLogic.asyncSetState({
            loading: true,
            error: false,
        })

        return this.props.updateVehicleStatus({
            variables: {
                vehicleId: vehicleId,
                status: status,
                soldInfo: soldInfo,
            }
        })
            .then(response => {
                actionLogic.asyncSetState({
                    loading: false,
                })

                return Promise.resolve(response)
            })
            .catch(error => {
                actionLogic.asyncSetState({
                    loading: false,
                    error: error,
                })

                return Promise.reject(error)
            })
    }

    updateVehicleImageCrop = async (actionLogic, vehicleId, cropData, imageId) => {
        const { currentUser, updateImageCropPosition } = this.props

        actionLogic.asyncSetState({
            loading: true,
            error: false,
        })

        return updateImageCropPosition({
            variables: {
                id: imageId,
                cropData: cropData,
            },
            refetchQueries: [{
                query: getVehicleProfileView,
                variables: {
                    vehicleId: vehicleId,
                    userId: currentUser.me.id,
                }
            }],
            awaitRefetchQueries: true
        })
            .then(response => {
                actionLogic.asyncSetState({
                    loading: false,
                })

                return Promise.resolve(response)
            })
            .catch(error => {
                actionLogic.asyncSetState({
                    loading: false,
                    error: error,
                })

                return Promise.reject(error)
            })
    }

    createSketch = async (actionLogic, vehicle, action, isVerifiedAd, isSpecialSuccessCase) => {
        actionLogic.asyncSetState({
            loading: true,
            error: false,
        })

        const vertical = getVehicleVertical(vehicle)

        return this.props.createAdSketchForVehicle({
            variables: {
                id: vehicle.id,
                vertical: vertical,
                action: action,
                generateVerifiedAdSketch: isVerifiedAd,
                isSpecialSuccessCase: isSpecialSuccessCase
            }
        })
            .then(response => {
                actionLogic.asyncSetState({
                    loading: false,
                })

                return Promise.resolve(response)
            })
            .catch(error => {
                actionLogic.asyncSetState({
                    loading: false,
                    error: error,
                })

                return Promise.reject(error)
            })
    }

    requestTrafiData = async (actionLogic, vehicleId) => {
        return actionLogic.callMutation(this.props.requestTrafiData, {
            variables: {
                vehicleId,
            }
        })
    }

    handleMount = (actionLogic) => {
        const { onMount } = this.props
        onMount && onMount(this.getRenderProps(actionLogic))
    }

    getRenderProps (actionLogic) {
        return {
            ...actionLogic,
            deleteVehicle: (...args) => this.deleteVehicle(actionLogic, ...args),
            updateVehiclePrefill: (...args) => this.updateVehiclePrefill(actionLogic, ...args),
            updateVehicle: (...args) => this.updateVehicle(actionLogic, ...args),
            updateVehicleStatus: (...args) => this.updateVehicleStatus(actionLogic, ...args),
            createSketch: (...args) => this.createSketch(actionLogic, ...args),
            requestTrafiData: (...args) => this.requestTrafiData(actionLogic, ...args),
            updateVehicleImageCrop: (...args) => this.updateVehicleImageCrop(actionLogic, ...args),
        }
    }

    render () {
        return (
            <ActionLogic onMount={this.handleMount}>
                {(actionLogic) => (
                    this.props.children(this.getRenderProps(actionLogic))
                )}
            </ActionLogic>
        )
    }
}

export default flowRight(
    graphqlCurrentUserCached,
    graphql(deleteVehicle, { name: 'deleteVehicle' }),
    graphql(updateVehiclePrefill, { name: 'updateVehiclePrefill' }),
    graphql(updateVehicleMutation, { name: 'updateVehicle' }),
    graphql(updateVehicleStatus, { name: 'updateVehicleStatus' }),
    graphql(createAdSketchForVehicle, { name: 'createAdSketchForVehicle' }),
    graphql(requestTrafiData, { name: 'requestTrafiData' }),
    graphql(updateImageCropPosition, { name: 'updateImageCropPosition' }),
)(VehicleProfileLogic)
